/**
 * BroadcastPage.jsx — admin broadcast composer.
 *
 * Replaces the old "raw HTML textarea" form with a three-mode editor:
 *   Write (WYSIWYG) · Plain text · HTML  — all converge on a single `html`
 *   payload, so the POST /api/admin/broadcast contract is unchanged.
 *
 * Pieces:
 *   - Audience picker   segmented control (By role / Specific emails /
 *                       Everyone) with role pills and an email chip input.
 *   - Editor            contentEditable toolbar (bold/italic/underline, lists,
 *                       links, text color, headings, insert-image-by-URL or
 *                       local upload), plus image sizing/alignment controls,
 *                       modeled on components/RichTextEditor.jsx but styled
 *                       with the admin palette so admin-theme.css light mode
 *                       keeps working.
 *   - Live preview      mock of the branded email shell (light card, header/
 *                       footer banners, footer note) rendered through a local
 *                       sanitizer — the shared RichText sanitizer strips IMG,
 *                       which broadcasts explicitly support, so this file keeps
 *                       its own whitelist (adds IMG with http(s) src only).
 *   - Confirm strip     inline confirm (audience + subject) before send,
 *                       replacing window.confirm.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  TextB, TextItalic, TextUnderline, ListBullets, ListNumbers, LinkSimple,
  TextAa, ImageSquare, UploadSimple, Trash, Eye, PaperPlaneTilt, WarningCircle, X,
} from '@phosphor-icons/react';
import api, { fileUrl } from '@/api';
import { stripHtml } from '@/components/RichTextEditor';

// ─────────────── content styles (lists/headings/placeholders) ───────────────
// Tailwind's preflight resets list markers and heading sizes — re-assert them
// for both the editor surface and the email preview.
const STYLE_ID = 'broadcast-richtext-styles';
const STYLES = `
.broadcast-richtext ul { list-style: disc; padding-left: 1.5rem; margin: 0.25rem 0; }
.broadcast-richtext ol { list-style: decimal; padding-left: 1.5rem; margin: 0.25rem 0; }
.broadcast-richtext li { margin: 0.125rem 0; }
.broadcast-richtext p { margin: 0.4rem 0; }
.broadcast-richtext h2 { font-size: 1.375rem; font-weight: 700; line-height: 1.3; margin: 0.6rem 0 0.3rem; }
.broadcast-richtext h3 { font-size: 1.0625rem; font-weight: 600; line-height: 1.35; margin: 0.5rem 0 0.25rem; }
.broadcast-richtext b, .broadcast-richtext strong { font-weight: 700 !important; }
.broadcast-richtext i, .broadcast-richtext em { font-style: italic !important; }
.broadcast-richtext u { text-decoration: underline; }
.broadcast-richtext s, .broadcast-richtext strike { text-decoration: line-through; }
.broadcast-richtext img { max-width: 100%; height: auto; border-radius: 6px; cursor: pointer; }
.broadcast-editor img[data-selected="1"] { outline: 2px solid #34d399; outline-offset: 2px; }
.broadcast-richtext a { text-decoration: underline; }
.broadcast-editor a { color: #6ee7b7; }
.broadcast-preview a { color: #7C3AED; }
.broadcast-editor:empty:before { content: attr(data-placeholder); color: #64748b; pointer-events: none; }
.broadcast-editor:focus { outline: none; }
`;

function injectStyles() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = STYLES;
  document.head.appendChild(el);
}

// ─────────────── preview sanitizer (whitelist + IMG) ───────────────
const PREVIEW_TAGS = new Set([
  'P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'DEL',
  'UL', 'OL', 'LI', 'SPAN', 'DIV', 'A', 'H1', 'H2', 'H3', 'IMG',
]);
const PREVIEW_STYLE = /^(font-size|font-weight|font-style|text-decoration|color|background-color|width|max-width|height|display|margin|margin-left|margin-right)$/;

function sanitizeForPreview(html) {
  if (typeof window === 'undefined' || !window.DOMParser) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const clean = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === 3) return;                    // text — keep
      if (child.nodeType !== 1) { child.remove(); return; } // comments etc — drop
      clean(child);
      const tag = child.tagName;
      if (!PREVIEW_TAGS.has(tag)) {                        // unwrap unknown tags
        const parent = child.parentNode;
        while (child.firstChild) parent.insertBefore(child.firstChild, child);
        parent.removeChild(child);
        return;
      }
      if (tag === 'IMG') {
        const source = child.getAttribute('src') || '';
        if (/^cid:/i.test(source)) {
          const previewUrl = fileUrl(source.slice(4));
          if (previewUrl) child.setAttribute('src', previewUrl);
        }
        if (!/^https?:\/\//i.test(child.getAttribute('src') || '')) {
          child.remove();
          return;
        }
      }
      Array.from(child.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (name === 'style') {
          const safe = child.style.cssText.split(';').map((s) => s.trim()).filter(Boolean)
            .filter((decl) => PREVIEW_STYLE.test(decl.split(':')[0].trim().toLowerCase()))
            .join('; ');
          if (safe) child.setAttribute('style', safe); else child.removeAttribute('style');
        } else if (tag === 'A' && name === 'href') {
          if (/^(https?:|mailto:)/i.test(attr.value.trim())) {
            child.setAttribute('rel', 'noreferrer');
            child.setAttribute('target', '_blank');
          } else child.removeAttribute('href');
        } else if (tag === 'IMG' && (name === 'src' || name === 'alt')) {
          // whitelisted above
        } else {
          child.removeAttribute(attr.name);
        }
      });
    });
  };
  clean(doc.body);
  return doc.body.innerHTML;
}

// ─────────────── mode conversion helpers ───────────────
const looksLikeHtml = (s) => /<[a-z][\s\S]*>/i.test(s);
const escapeHtml = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const plainToHtml = (text) => {
  const t = (text || '').trim();
  if (!t) return '';
  return t.split(/\n{2,}/)
    .map((para) => `<p>${escapeHtml(para).replace(/\n/g, '<br/>')}</p>`)
    .join('');
};

const htmlToPlain = (html) => {
  if (!html) return '';
  if (!looksLikeHtml(html) || typeof window === 'undefined' || !window.DOMParser) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const BLOCKS = new Set(['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'UL', 'OL']);
  const walk = (node) => {
    let out = '';
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) out += child.textContent;
      else if (child.nodeType === 1) {
        if (child.tagName === 'BR') out += '\n';
        else {
          out += walk(child);
          if (BLOCKS.has(child.tagName)) out += '\n';
        }
      }
    });
    return out;
  };
  return walk(doc.body).replace(/\n{3,}/g, '\n\n').trim();
};

// ─────────────── shared bits ───────────────
const inputCls = 'w-full bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-500/50';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Small admin-styled modal used by the link + image prompts.
const PromptModal = ({ title, hint, onClose, onSubmit, submitLabel, canSubmit, children, testid }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onMouseDown={onClose} data-testid={testid}>
    <div className="bg-[#111827] border border-slate-800 rounded-xl w-full max-w-sm p-5 shadow-xl" onMouseDown={(e) => e.stopPropagation()}>
      <div className="text-sm font-semibold mb-1">{title}</div>
      {hint && <p className="text-[11px] text-slate-500 mb-3">{hint}</p>}
      {children}
      <div className="flex justify-end gap-2 mt-4">
        <button type="button" onClick={onClose}
          className="px-3 py-2 text-sm font-medium border border-slate-800 rounded-lg text-slate-400 hover:text-slate-100"
          data-testid={`${testid}-cancel`}>Cancel</button>
        <button type="button" onClick={onSubmit} disabled={!canSubmit}
          className="px-4 py-2 text-sm font-semibold bg-emerald-500 hover:bg-emerald-400 text-black rounded-lg disabled:opacity-50"
          data-testid={`${testid}-submit`}>{submitLabel}</button>
      </div>
    </div>
  </div>
);

// ─────────────── Write mode: admin-styled WYSIWYG ───────────────
const TEXT_SIZES = [
  { label: 'Text', block: 'P' },
  { label: 'Subheader', block: 'H3' },
  { label: 'Header', block: 'H2' },
];

const WriteEditor = ({ html, onChange, nonce }) => {
  const wrapperRef = useRef(null);
  const ref = useRef(null);
  const savedRange = useRef(null);
  const imageInputRef = useRef(null);
  const selectedImgRef = useRef(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [imgOpen, setImgOpen] = useState(false);
  const [imgTab, setImgTab] = useState('upload');
  const [imgUrl, setImgUrl] = useState('');
  const [imgWidth, setImgWidth] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImg, setSelectedImg] = useState(null);
  const [, refreshImageControls] = useState(0);

  useEffect(injectStyles, []);

  // Seed on mount / when re-entering Write mode (nonce bump). Uncontrolled
  // afterwards so the caret never jumps mid-typing.
  useEffect(() => {
    if (ref.current) {
      const seed = document.createElement('div');
      seed.innerHTML = html || '';
      seed.querySelectorAll('img[src^="cid:"]').forEach((image) => {
        const fileId = image.getAttribute('src').slice(4);
        const previewUrl = fileUrl(fileId);
        if (previewUrl) {
          image.setAttribute('src', previewUrl);
          image.setAttribute('data-inline-file-id', fileId);
        }
      });
      ref.current.innerHTML = seed.innerHTML;
    }
    selectedImgRef.current = null;
    setSelectedImg(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce]);

  useEffect(() => {
    const clearWhenOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        selectedImgRef.current?.removeAttribute('data-selected');
        selectedImgRef.current = null;
        setSelectedImg(null);
      }
    };
    document.addEventListener('mousedown', clearWhenOutside);
    return () => document.removeEventListener('mousedown', clearWhenOutside);
  }, []);

  // Keep the selection marker editor-only. It must never enter the broadcast HTML.
  const emit = () => {
    const cleanEditor = ref.current?.cloneNode(true);
    cleanEditor?.querySelectorAll('[data-selected]').forEach((node) => node.removeAttribute('data-selected'));
    cleanEditor?.querySelectorAll('img[data-inline-file-id]').forEach((image) => {
      const fileId = image.getAttribute('data-inline-file-id');
      image.setAttribute('src', `cid:${fileId}`);
      image.removeAttribute('data-inline-file-id');
    });
    onChange(cleanEditor?.innerHTML || '');
  };
  const exec = (cmd, arg) => { ref.current?.focus(); document.execCommand(cmd, false, arg); emit(); };

  // Popups/pickers blur the editor and collapse the selection — snapshot and
  // restore the range around them (same trick as the shared RichTextEditor).
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };
  const restoreSelection = () => {
    ref.current?.focus();
    const sel = window.getSelection();
    if (savedRange.current && sel) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
  };

  const applyColor = (value) => {
    restoreSelection();
    document.execCommand('styleWithCSS', false, true);
    document.execCommand('foreColor', false, value);
    document.execCommand('styleWithCSS', false, false);
    emit();
  };

  const openLink = () => { saveSelection(); setLinkText(savedRange.current?.toString() || ''); setLinkUrl(''); setLinkOpen(true); };
  const submitLink = () => {
    const url = linkUrl.trim();
    if (!url) return;
    setLinkOpen(false);
    restoreSelection();
    const label = (linkText || url).trim();
    document.execCommand('insertHTML', false, `<a href="${escapeHtml(url)}">${escapeHtml(label)}</a>`);
    emit();
  };

  const openImage = () => { saveSelection(); setImgTab('upload'); setImgUrl(''); setImgWidth(''); setImgOpen(true); };
  const insertImage = (url, alt = '', width, withDefaultLayout = false, inlineFileId = '') => {
    restoreSelection();
    const widthDecl = width >= 10 && width <= 100 ? `width:${width}%;` : '';
    const layoutDecl = withDefaultLayout ? 'display:block;margin:12px 0;' : '';
    const fileMarker = inlineFileId ? ` data-inline-file-id="${escapeHtml(inlineFileId)}"` : '';
    document.execCommand('insertHTML', false, `<img src="${escapeHtml(url)}"${fileMarker} alt="${escapeHtml(alt)}" style="${widthDecl}max-width:100%;height:auto;${layoutDecl}" />`);
    emit();
  };
  const submitImage = () => {
    const url = imgUrl.trim();
    if (!/^https?:\/\//i.test(url)) return;
    setImgOpen(false);
    const w = parseInt(imgWidth, 10);
    insertImage(url, '', w);
  };
  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error('Image must be under 4 MB.');
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/files/upload', formData);
      const url = fileUrl(response.data.file_id);
      if (!url) throw new Error('Upload did not return an image URL');
      setImgOpen(false);
      insertImage(url, file.name, 100, true, response.data.file_id);
      toast.success('Image added to the message.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Image upload failed.');
    } finally {
      setUploadingImage(false);
    }
  };
  const selectImage = (image) => {
    selectedImgRef.current?.removeAttribute('data-selected');
    image.setAttribute('data-selected', '1');
    selectedImgRef.current = image;
    setSelectedImg(image);
  };
  const clearSelectedImage = () => {
    selectedImgRef.current?.removeAttribute('data-selected');
    selectedImgRef.current = null;
    setSelectedImg(null);
  };
  const updateImage = (styles) => {
    const image = selectedImgRef.current;
    if (!image) return;
    Object.entries(styles).forEach(([property, value]) => { image.style[property] = value; });
    refreshImageControls((version) => version + 1);
    emit();
  };
  const removeSelectedImage = () => {
    const image = selectedImgRef.current;
    if (!image) return;
    image.remove();
    clearSelectedImage();
    emit();
  };

  const tools = [
    { icon: TextB, cmd: 'bold', title: 'Bold' },
    { icon: TextItalic, cmd: 'italic', title: 'Italic' },
    { icon: TextUnderline, cmd: 'underline', title: 'Underline' },
    { icon: ListBullets, cmd: 'insertUnorderedList', title: 'Bulleted list' },
    { icon: ListNumbers, cmd: 'insertOrderedList', title: 'Numbered list' },
  ];
  const toolBtn = 'w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:text-emerald-300 hover:bg-emerald-500/10 transition-colors';

  return (
    <div ref={wrapperRef} className="bg-[#0a0e14] border border-slate-800 rounded-lg overflow-hidden focus-within:border-emerald-500/50" data-testid="broadcast-write">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-800 flex-wrap">
        {tools.map((t) => (
          <button key={t.cmd} type="button" title={t.title}
            onMouseDown={(e) => { e.preventDefault(); exec(t.cmd); }}
            className={toolBtn} data-testid={`broadcast-tool-${t.cmd}`}>
            <t.icon size={15} weight="bold" />
          </button>
        ))}
        <span className="w-px h-4 bg-slate-800 mx-1" />
        <select title="Text size" defaultValue=""
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => { const v = e.target.value; if (v) exec('formatBlock', v); e.target.value = ''; }}
          className="bg-[#0a0e14] border border-slate-800 rounded px-1.5 py-1 text-xs text-slate-300 focus:outline-none focus:border-emerald-500/50 cursor-pointer"
          data-testid="broadcast-tool-size">
          <option value="" disabled>Size</option>
          {TEXT_SIZES.map((f) => <option key={f.block} value={f.block}>{f.label}</option>)}
        </select>
        <span className="w-px h-4 bg-slate-800 mx-1" />
        <button type="button" title="Add link"
          onMouseDown={(e) => { e.preventDefault(); openLink(); }}
          className={toolBtn} data-testid="broadcast-tool-link">
          <LinkSimple size={15} weight="bold" />
        </button>
        <label title="Text color" onMouseDown={saveSelection}
          className={`relative cursor-pointer ${toolBtn}`}>
          <TextAa size={15} weight="bold" />
          <input type="color" defaultValue="#34d399"
            onMouseDown={saveSelection}
            onChange={(e) => applyColor(e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            data-testid="broadcast-tool-color" />
        </label>
        <button type="button" title="Insert image by URL"
          onMouseDown={(e) => { e.preventDefault(); openImage(); }}
          className={toolBtn} data-testid="broadcast-tool-image">
          <ImageSquare size={15} weight="bold" />
        </button>
      </div>
      {selectedImg && (
        <div className="flex items-center gap-2 px-2 py-1.5 border-b border-emerald-500/20 bg-emerald-500/5 flex-wrap text-[11px]" data-testid="broadcast-image-controls">
          <span className="uppercase tracking-wider text-emerald-300 font-bold">Image</span>
          <span className="text-slate-500">Size</span>
          {[25, 50, 75, 100].map((width) => (
            <button key={width} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => updateImage({ width: `${width}%`, maxWidth: '100%' })}
              className={`px-1.5 py-1 rounded ${parseInt(selectedImg.style.width, 10) === width ? 'bg-emerald-500/20 text-emerald-300' : 'text-slate-400 hover:text-slate-100'}`}
              data-testid={`broadcast-image-width-${width}`}>
              {width}%
            </button>
          ))}
          <span className="w-px h-4 bg-slate-800 mx-1" />
          <span className="text-slate-500">Align</span>
          {[['left', '0 12px 12px 0'], ['center', '12px auto'], ['right', '0 0 12px auto']].map(([alignment, margin]) => (
            <button key={alignment} type="button" title={`Align ${alignment}`} onMouseDown={(e) => e.preventDefault()}
              onClick={() => updateImage({ display: 'block', margin })}
              className="px-1.5 py-1 rounded text-slate-400 hover:text-emerald-300 capitalize" data-testid={`broadcast-image-align-${alignment}`}>
              {alignment}
            </button>
          ))}
          <button type="button" title="Remove image" onMouseDown={(e) => e.preventDefault()} onClick={removeSelectedImage}
            className="ml-auto inline-flex items-center gap-1 px-1.5 py-1 rounded text-red-400 hover:bg-red-500/10" data-testid="broadcast-image-remove">
            <Trash size={12} weight="bold" /> Remove
          </button>
        </div>
      )}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder="Write your announcement…"
        onInput={emit}
        onClick={(event) => {
          if (event.target.tagName === 'IMG') selectImage(event.target);
          else clearSelectedImage();
        }}
        onKeyDown={(event) => { if (event.key === 'Escape') clearSelectedImage(); }}
        className="broadcast-richtext broadcast-editor min-h-[220px] max-h-[420px] overflow-y-auto px-3 py-2.5 text-sm text-slate-100 leading-relaxed"
        data-testid="broadcast-write-editor"
      />

      {linkOpen && (
        <PromptModal title="Add link" hint="Links open in a new tab." testid="broadcast-link-prompt"
          onClose={() => setLinkOpen(false)} onSubmit={submitLink} submitLabel="Add link" canSubmit={!!linkUrl.trim()}>
          <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">Text to display</label>
          <input autoFocus type="text" value={linkText} onChange={(e) => setLinkText(e.target.value)}
            placeholder="e.g. Read the announcement" className={inputCls} data-testid="broadcast-link-text" />
          <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 mt-3">URL</label>
          <input type="url" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitLink(); } }}
            placeholder="https://example.com" className={inputCls} data-testid="broadcast-link-url" />
        </PromptModal>
      )}

      {imgOpen && (
        <PromptModal title="Insert image" hint="Upload from your computer or use an image URL." testid="broadcast-image-prompt"
          onClose={() => setImgOpen(false)} onSubmit={imgTab === 'upload' ? () => imageInputRef.current?.click() : submitImage}
          submitLabel={imgTab === 'upload' ? 'Choose image' : 'Insert image'}
          canSubmit={imgTab === 'upload' ? !uploadingImage : /^https?:\/\//i.test(imgUrl.trim())}>
          <div className="flex gap-1 bg-[#0a0e14] border border-slate-800 rounded-lg p-1 mb-4" role="tablist">
            {[['upload', 'Upload from computer'], ['url', 'Use image URL']].map(([tab, label]) => (
              <button key={tab} type="button" role="tab" aria-selected={imgTab === tab} onClick={() => setImgTab(tab)}
                className={segBtn(imgTab === tab) + ' flex-1'} data-testid={`broadcast-image-tab-${tab}`}>
                {label}
              </button>
            ))}
          </div>
          {imgTab === 'upload' ? (
            <div className="border border-dashed border-slate-700 rounded-lg p-5 text-center">
              <UploadSimple size={24} className="mx-auto mb-2 text-emerald-400" weight="bold" />
              <div className="text-sm text-slate-200">Choose an image from your computer</div>
              <p className="text-[11px] text-slate-500 mt-1">PNG, JPG, GIF, SVG, or WebP · up to 4 MB</p>
              <input ref={imageInputRef} type="file" accept="image/*" className="hidden" data-testid="broadcast-image-file"
                onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; uploadImage(file); }} />
              {uploadingImage && <p className="text-xs text-emerald-300 mt-3">Uploading image…</p>}
            </div>
          ) : (
            <>
              <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">Image URL</label>
              <input autoFocus type="url" value={imgUrl} onChange={(e) => setImgUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitImage(); } }}
                placeholder="https://example.com/banner.png" className={inputCls} data-testid="broadcast-image-url" />
              <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 mt-3">Width % (optional)</label>
              <input type="number" min="10" max="100" value={imgWidth} onChange={(e) => setImgWidth(e.target.value)}
                placeholder="100" className={inputCls} data-testid="broadcast-image-width" />
            </>
          )}
        </PromptModal>
      )}
    </div>
  );
};

// ─────────────── Specific emails: chip input ───────────────
const EmailChips = ({ emails, setEmails }) => {
  const [draft, setDraft] = useState('');
  const add = (raw) => {
    const value = raw.trim().toLowerCase();
    if (!value) return;
    if (!emails.includes(value)) setEmails([...emails, value]);
  };
  const addDraft = () => { add(draft); setDraft(''); };
  const onChange = (e) => {
    const v = e.target.value;
    if (v.includes(',') || v.includes('\n') || v.includes(' ')) {
      v.split(/[,\s]+/).forEach(add);
      setDraft('');
    } else setDraft(v);
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addDraft(); }
    else if (e.key === 'Backspace' && !draft && emails.length) setEmails(emails.slice(0, -1));
  };

  return (
    <div className="bg-[#0a0e14] border border-slate-800 rounded-lg px-2 py-2 flex flex-wrap items-center gap-1.5 focus-within:border-emerald-500/50" data-testid="broadcast-emails-wrap">
      {emails.map((email) => {
        const valid = EMAIL_RE.test(email);
        return (
          <span key={email}
            className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md border ${valid ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-red-500/10 border-red-500/40 text-red-400'}`}
            title={valid ? email : 'Invalid email address'}>
            {email}
            <button type="button" onClick={() => setEmails(emails.filter((x) => x !== email))}
              className="hover:opacity-70" data-testid={`broadcast-email-remove`} aria-label={`Remove ${email}`}>
              <X size={11} weight="bold" />
            </button>
          </span>
        );
      })}
      <input
        value={draft}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onBlur={addDraft}
        placeholder={emails.length ? '' : 'user1@ex.com, user2@ex.com — Enter to add'}
        className="flex-1 min-w-[220px] bg-transparent px-1 py-1 text-sm text-slate-100 focus:outline-none"
        data-testid="broadcast-emails"
      />
    </div>
  );
};

// ─────────────── live preview (mock of the branded shell) ───────────────
const HEADER_IMG = 'https://founderboard.io/FounderBoard-Header.svg';
const FOOTER_IMG = 'https://founderboard.io/FounderBoard-Footer.svg';
const hideOnError = (e) => { e.currentTarget.style.display = 'none'; };

const EmailPreview = ({ subject, html }) => {
  const clean = useMemo(() => sanitizeForPreview(html), [html]);
  return (
    <div style={{ background: '#F8FAFC', borderRadius: 12, padding: '20px 12px' }}>
      <div className="mx-auto" style={{ maxWidth: 560 }}>
        <div style={{ fontSize: 12, color: '#64748B', padding: '0 4px 10px' }}>
          <span style={{ fontWeight: 600, color: '#475569' }}>Subject:</span> {subject.trim() || <em>No subject yet</em>}
        </div>
        <div style={{ background: '#ffffff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,23,42,.08)' }}>
          <img src={HEADER_IMG} alt="Founderboard" onError={hideOnError} style={{ display: 'block', width: '100%', height: 'auto' }} />
          <div style={{ padding: '26px 30px 30px', fontSize: 15, lineHeight: 1.6, color: '#0F172A' }}>
            {stripHtml(clean) || /<img/i.test(clean)
              ? <div className="broadcast-richtext broadcast-preview" dangerouslySetInnerHTML={{ __html: clean }} />
              : <span style={{ color: '#94A3B8' }}>Your message will appear here…</span>}
          </div>
          <div style={{ padding: '0 30px 16px', fontSize: 12, color: '#64748B', lineHeight: 1.55 }}>
            You're receiving this because you have an account on Founderboard.
          </div>
          <img src={FOOTER_IMG} alt="" onError={hideOnError} style={{ display: 'block', width: '100%', height: 'auto' }} />
        </div>
      </div>
    </div>
  );
};

// ─────────────── page ───────────────
const MODES = [
  { id: 'write', label: 'Write' },
  { id: 'plain', label: 'Plain text' },
  { id: 'html', label: 'HTML' },
];
const SCOPES = [
  { id: 'role', label: 'By role' },
  { id: 'user_emails', label: 'Specific emails' },
  { id: 'all', label: 'Everyone' },
];
const ROLES = ['startup', 'vc', 'vendor', 'admin'];

const segBtn = (active) =>
  `px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${active ? 'bg-emerald-500/10 text-emerald-300' : 'text-slate-400 hover:text-slate-100'}`;

const BroadcastPage = () => {
  const [scope, setScope] = useState('role');
  const [role, setRole] = useState('startup');
  const [emails, setEmails] = useState([]);
  const [subject, setSubject] = useState('');
  const [mode, setMode] = useState('write');
  const [html, setHtml] = useState('');
  const [plain, setPlain] = useState('');
  const [nonce, setNonce] = useState(0);          // reseeds the uncontrolled editor on mode entry
  const [mobilePreview, setMobilePreview] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(injectStyles, []);

  // Single canonical value for preview + send, regardless of editing mode.
  const effectiveHtml = useMemo(() => (mode === 'plain' ? plainToHtml(plain) : html), [mode, plain, html]);

  const invalidCount = emails.filter((e) => !EMAIL_RE.test(e)).length;
  const bodyEmpty = !stripHtml(effectiveHtml) && !/<img/i.test(effectiveHtml);
  const canSend = !!subject.trim() && !bodyEmpty
    && (scope !== 'user_emails' || (emails.length > 0 && invalidCount === 0));

  const audience = scope === 'all'
    ? 'everyone on Founderboard'
    : scope === 'role'
      ? `all users with the “${role}” role`
      : `${emails.length} specific recipient${emails.length === 1 ? '' : 's'}`;

  // Conversions on tab switch keep the other editors in sync.
  const switchMode = (next) => {
    if (next === mode) return;
    if (mode === 'plain') setHtml(plainToHtml(plain));
    if (next === 'plain') setPlain(htmlToPlain(html));
    if (next === 'write') setNonce((n) => n + 1);
    setMode(next);
  };

  const requestSend = () => {
    if (!subject.trim()) return toast.error('Add a subject first.');
    if (bodyEmpty) return toast.error('Write a message first.');
    if (scope === 'user_emails' && !emails.length) return toast.error('Add at least one recipient email.');
    if (scope === 'user_emails' && invalidCount) return toast.error('Fix the invalid email addresses first.');
    setConfirming(true);
  };

  const doSend = async () => {
    setBusy(true);
    try {
      const payload = { subject: subject.trim(), html: effectiveHtml, scope };
      if (scope === 'role') payload.role = role;
      if (scope === 'user_emails') payload.user_emails = emails;
      const r = await api.post('/admin/broadcast', payload);
      toast.success(`Sent ${r.data.sent} · Failed ${r.data.failed}`);
      setConfirming(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px]" data-testid="broadcast-page">
      {/* Header mirrors AdminApp's shared <Page> (section kicker + divider). */}
      <div className="flex items-start justify-between mb-6 pb-5 gap-4 flex-wrap border-b border-slate-800">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1">
            <span className="text-slate-500">Admin</span><span className="text-slate-600 mx-1.5">/</span><span className="text-emerald-400">Programs &amp; outreach</span>
          </div>
          <h1 className="text-2xl font-heading font-bold" style={{ letterSpacing: '-0.02em' }}>Broadcast</h1>
          <p className="text-sm text-slate-400 mt-1">Send an announcement to a segment.</p>
        </div>
        <button type="button" onClick={() => setMobilePreview((v) => !v)}
          className="xl:hidden flex items-center gap-2 text-xs font-semibold border border-slate-800 rounded-lg px-3 py-2 text-slate-400 hover:text-slate-100"
          data-testid="broadcast-preview-toggle">
          <Eye size={13} weight="bold" /> {mobilePreview ? 'Hide preview' : 'Preview'}
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
        {/* ── composer ── */}
        <div className="bg-[#111827] border border-slate-800 rounded-xl p-5 space-y-4">
          {/* audience */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">Audience</div>
            <div className="flex gap-1 bg-[#0a0e14] border border-slate-800 rounded-lg p-1 w-fit" data-testid="broadcast-scope">
              {SCOPES.map((s) => (
                <button key={s.id} type="button" onClick={() => { setScope(s.id); setConfirming(false); }}
                  className={segBtn(scope === s.id)} data-testid={`broadcast-scope-${s.id}`}>
                  {s.label}
                </button>
              ))}
            </div>
            {scope === 'role' && (
              <div className="flex gap-1.5 mt-3 flex-wrap" data-testid="broadcast-role">
                {ROLES.map((r) => (
                  <button key={r} type="button" onClick={() => setRole(r)}
                    className={segBtn(role === r) + ' border ' + (role === r ? 'border-emerald-500/40' : 'border-slate-800')}
                    data-testid={`broadcast-role-${r}`}>
                    {r}
                  </button>
                ))}
              </div>
            )}
            {scope === 'user_emails' && (
              <div className="mt-3">
                <EmailChips emails={emails} setEmails={setEmails} />
                {invalidCount > 0 && <p className="text-[11px] text-red-400 mt-1.5">{invalidCount} invalid address{invalidCount === 1 ? '' : 'es'} — fix or remove before sending.</p>}
              </div>
            )}
            <p className="text-[11px] text-slate-500 mt-2.5">
              Sending to <span className="text-emerald-300 font-medium">{audience}</span>.
            </p>
          </div>

          {/* subject */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Subject</div>
              <div className={`text-[10px] ${subject.length > 60 ? 'text-amber-400' : 'text-slate-500'}`}>
                {subject.length} chars{subject.length > 60 ? ' — under 60 recommended' : ''}
              </div>
            </div>
            <input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)}
              className={inputCls} data-testid="broadcast-subject" />
          </div>

          {/* body */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Message</div>
              <div className="flex gap-1 bg-[#0a0e14] border border-slate-800 rounded-lg p-1" data-testid="broadcast-mode">
                {MODES.map((m) => (
                  <button key={m.id} type="button" onClick={() => switchMode(m.id)}
                    className={segBtn(mode === m.id)} data-testid={`broadcast-mode-${m.id}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            {mode === 'write' && <WriteEditor html={html} onChange={setHtml} nonce={nonce} />}
            {mode === 'plain' && (
              <textarea rows={10} placeholder="Just type — paragraphs and line breaks are preserved."
                value={plain} onChange={(e) => setPlain(e.target.value)}
                className={inputCls + ' min-h-[236px] resize-y'} data-testid="broadcast-plain" />
            )}
            {mode === 'html' && (
              <textarea rows={10} placeholder="HTML body"
                value={html} onChange={(e) => setHtml(e.target.value)}
                className={inputCls + ' min-h-[236px] resize-y font-mono'} data-testid="broadcast-html" />
            )}
          </div>

          {/* confirm strip + send */}
          {confirming && (
            <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-4 flex items-start gap-3" data-testid="broadcast-confirm">
              <WarningCircle size={18} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">Ready to send?</div>
                <p className="text-xs text-slate-400 mt-1 break-words">
                  “{subject.trim()}” will go to <span className="text-amber-300">{audience}</span>. This can't be undone.
                </p>
                <div className="flex gap-2 mt-3">
                  <button type="button" onClick={doSend} disabled={busy}
                    className="bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-50"
                    data-testid="broadcast-confirm-send">
                    {busy ? 'Sending…' : 'Confirm & send'}
                  </button>
                  <button type="button" onClick={() => setConfirming(false)} disabled={busy}
                    className="border border-slate-800 text-xs font-semibold px-3 py-2 rounded-lg text-slate-400 hover:text-slate-100 disabled:opacity-50"
                    data-testid="broadcast-confirm-cancel">
                    Keep editing
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <button type="button" onClick={requestSend} disabled={busy || !canSend}
              className="bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2"
              data-testid="broadcast-send">
              <PaperPlaneTilt size={14} weight="bold" /> Send broadcast
            </button>
            {!canSend && !busy && (
              <span className="text-[11px] text-slate-500">
                {!subject.trim() ? 'Add a subject to enable sending.' : bodyEmpty ? 'Write a message to enable sending.' : 'Add valid recipients to enable sending.'}
              </span>
            )}
          </div>
        </div>

        {/* ── preview ── */}
        <div className={`bg-[#111827] border border-slate-800 rounded-xl p-5 xl:sticky xl:top-6 ${mobilePreview ? '' : 'hidden xl:block'}`} data-testid="broadcast-preview">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-3">Live preview</div>
          <EmailPreview subject={subject} html={effectiveHtml} />
        </div>
      </div>
    </div>
  );
};

export default BroadcastPage;
